import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAutoTranslateString } from '../hooks/useAutoTranslateString';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdBanner = () => {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/ads/active`).then(r => setItems(r.data?.items || [])).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (items.length === 0) return;
    const current = items[idx];
    const duration = Math.max(2, current?.duration_seconds || 8) * 1000;
    timerRef.current = setTimeout(() => setIdx(i => (i + 1) % items.length), duration);
    return () => clearTimeout(timerRef.current);
  }, [items, idx]);

  if (items.length === 0) return null;

  const current = items[idx];
  const isVideo = (current.media_url || '').match(/\.(mp4|webm|mov)$/i);

  return (
    <div className="bg-[#FAFAF8] border-b border-[rgba(28,36,30,0.06)]" data-testid="ad-banner">
      <div className="max-w-7xl mx-auto relative">
        <div className="aspect-[16/4] sm:aspect-[16/3] md:aspect-[20/3] overflow-hidden flex items-center justify-center bg-black">
          {isVideo ? (
            <video
              key={current.id}
              src={current.media_url}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
              data-testid={`ad-video-${current.id}`}
            />
          ) : (
            <a href={current.link_url || '#'} target={current.link_url ? '_blank' : '_self'} rel="noopener noreferrer" className="w-full h-full">
              <img
                key={current.id}
                src={current.media_url}
                alt={current.title || 'Promotion'}
                className="w-full h-full object-cover"
                data-testid={`ad-image-${current.id}`}
              />
            </a>
          )}
          {current.title && (
            <BannerTitle title={current.title} />
          )}
        </div>
        {items.length > 1 && (
          <>
            <button onClick={() => setIdx(i => (i - 1 + items.length) % items.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full" aria-label="Previous">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => setIdx(i => (i + 1) % items.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full" aria-label="Next">
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
              {items.map((it, i) => (
                <span key={it.id || `dot-${i}`} className={`w-1.5 h-1.5 rounded-full ${i === idx ? 'bg-white' : 'bg-white/40'}`} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};


const BannerTitle = ({ title }) => {
  const translated = useAutoTranslateString(title);
  return (
    <div className="absolute bottom-3 left-4 right-4 sm:left-8 sm:right-8 bg-black/50 text-white px-3 py-1.5 rounded backdrop-blur-sm text-sm font-medium">
      {translated}
    </div>
  );
};
